/**
=========================================================
* Soft UI Dashboard React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { useCallback, useState, forwardRef } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";

import { useSoftUIController, driverUpdated } from "context";
import moment from 'moment';

// Soft UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MiniStatisticsCard from "examples/MiniStatisticsCard";
import DataTable from "examples/Tables/DataTable";
import DriverMap from './components/DriverMap';

import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

// Data
import UnassignedTasks from "layouts/dashboard/components/UnassignedTasks";
import { driverData, taskData } from "layouts/softDashboard/data/dataTableData";
import { assignTask } from "actions";
import { unassignedTaskData } from "./data/dataTableData";
import SoftTypography from "components/SoftTypography";

function Dashboard() {
  const [controller, dispatch] = useSoftUIController();
  const { drivers, deliveries, pickups, currentDate, user, selectedDriver } = controller;
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toaster, setToaster] = useState(false);

  const activeDrivers = drivers.filter(d => (deliveries.find(ds => ds.driver === d.email) || pickups.find(ps => ps.driver === d.email)));
  const idleDrivers = drivers.filter(d => d.driver && !activeDrivers.find(ad => ad.email === d.email));

  const handleClose = () => {
    setMapModalVisible(false);
  }

  const closeToaster = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToaster(false);
  };

  const handleAssignTask = (task) => {
    setSelectedTask(task);
    setAssignModalVisible(true);
  }

  const renderRowSubComponent = useCallback(({ row }) => {
    const data = row.original;
    let taskInfo = deliveries.filter(d => d.driver === data.email).concat(pickups.filter(p => p.driver === data.email));
    return (<DataTable dataColumns={taskData} dataRows={taskInfo} allowPagination={false} entriesPerPage={{ defaultValue: taskInfo.length, entries: [] }}
      title={{ text: (data ? "Tasks for: " + data.label + " (" + data.email + ")" : ""), variant: "body2", color: "info", weight: 'medium', left: '3%' }} />);
  }, [deliveries, pickups]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: 'Deliveries (Complete/Total)'}}
                count={`${user ? `${deliveries.filter(d => d.division === user.shipFrom && d.completed).length}/${deliveries.filter(d => d.division === user.shipFrom).length}` : ''}`}
                icon={{ color: "info", component: "directions_bus" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: 'Pickups (Complete/Total)' }}
                count={`${user ? `${pickups.filter(p => p.division === user.shipFrom && p.completed).length}/${pickups.filter(p => p.division === user.shipFrom).length}` : ''}`}
                icon={{ color: "info", component: "view_in_ar" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Active Drivers" }}
                count={activeDrivers.length}
                icon={{ color: "info", component: "people" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Idle Drivers" }}
                count={idleDrivers.length}
                icon={{ color: "info", component: "people_outline" }}
              />
            </Grid>
          </Grid>
        </SoftBox>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
            <DataTable dataColumns={unassignedTaskData(handleAssignTask)}
                dataRows={deliveries.filter(d => {
                  if(user) {
                    if (user.shipFrom === 'PGH') {
                      return d.deliveryMethod === 'FEI Delivery' && !d.taskId;                        
                    }
                    else if(user.shipFrom === 'PA2')
                    {             
                        if(d.orders.find(o => o.id === 219678)) {
                          console.log(d.orders.map(o => o.id), d.deliveryMethod === `BEX2 - FEI Delivery` && !d.taskId);
                        }                        
                        
                        return d.deliveryMethod === `BEX2 - FEI Delivery` && !d.taskId;                      
                    }
                    else
                    {                     
                      return d.deliveryMethod === `B${user.shipFrom} - FEI Delivery` && !d.taskId;                      
                    }
                  }
                  else {
                    return false;
                  }                                                                       
                }).concat(pickups.filter(p => !p.taskId && p.division === user?.shipFrom))} title={{ text: "Unassigned Tasks" }} />
            </Grid>
          </Grid>
        </SoftBox>         
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <DataTable dataColumns={driverData(deliveries, pickups, setMapModalVisible, dispatch, user, setToaster, currentDate)}
                dataRows={activeDrivers.filter(d => d.shipFrom === user?.shipFrom)} renderRowSubComponent={renderRowSubComponent} title={{ text: "Pickup and Delivery Tasks" }} />
            </Grid>
          </Grid>
        </SoftBox>
        <Modal
          open={mapModalVisible}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <SoftBox sx={modalStyle}>
            <DriverMap driver={selectedDriver ? selectedDriver : null} />
          </SoftBox>
        </Modal>
        <Dialog fullWidth={true} maxWidth="md" onClose={() => setAssignModalVisible(false)} open={assignModalVisible}>
          <DialogTitle style={{ borderBottom: '1px solid #CCC' }}>{user ? `Assign Task - ${user.shipFrom}` : 'Assign Task'}</DialogTitle>
          <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
              <SoftSelect options={drivers.filter(d => d.shipFrom === user?.shipFrom)} getOptionValue={(d) => d.email} onChange={(value) => setDriver(value) } menuPosition="fixed"/>                    
          </DialogContent>
          <DialogActions>
            <SoftButton color="secondary" onClick={() => setAssignModalVisible(false)}>Close</SoftButton>
            <SoftButton disabled={loading} onClick={async () => {
              if(driver && selectedTask)
              {
                setLoading(true);
                try { 
                  let result = await assignTask(driver, user.email, selectedTask);
                  console.log('task assigned', result);

                  let updatedDeliveries = deliveries.map(d => {
                    let delivery = result.deliveries.find(rd => d.orders[0].id === rd.orders[0].id);                       
                    return delivery ? delivery : d;
                  });

                  let updatedPickups = pickups.map(p => {
                    let pickup = result.pickups.find(rp => rp.returnAuthId === p.returnAuthId);
                    return pickup ? pickup : p;
                  });

                  driverUpdated(dispatch, { deliveries: updatedDeliveries, pickups: updatedPickups });                                    
                  setLoading(false);
                  setAssignModalVisible(false);                  
                  setToaster(true);
                }
                catch(e) {
                  console.error(e);
                  setLoading(false);
                }
                             
              }
              else
              {
                alert('You muset select a driver');
              }            
            }}>{loading ? 'Loading...' : 'Submit'}
            </SoftButton>
          </DialogActions>
        </Dialog>     

      </SoftBox>
      <Snackbar open={toaster} autoHideDuration={3000} onClose={closeToaster}>
        <Alert onClose={closeToaster} severity="success" sx={{ width: '100%' }}>
          The operation completed successfully!
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Dashboard;